import React from 'react';
import { useTranslation } from '../../../../i18n/hooks/useTranslation';

interface ChipGroup {
  id: string;
  labelKey: string;
  options: { value: string; labelKey: string }[];
}

interface ChipsQuestionProps {
  questionNumber: number;
  labelKey: string;
  groups: ChipGroup[];
  /** Map of groupId → selected value (or null). */
  value: Record<string, string | null>;
  onChange: (groupId: string, value: string | null) => void;
}

export function ChipsQuestion({
  questionNumber,
  labelKey,
  groups,
  value,
  onChange,
}: ChipsQuestionProps) {
  const { t } = useTranslation();

  return (
    <div className="bg-white rounded-xl p-4 mb-3 shadow-sm">
      <div className="flex items-center gap-1.5 mb-3">
        <span className="inline-flex items-center justify-center w-[22px] h-[22px] rounded-full bg-amber-500 text-white text-xs font-medium shrink-0">
          {questionNumber}
        </span>
        <span className="text-[15px] font-semibold text-gray-800">{t(labelKey)}</span>
      </div>

      {groups.map((group, idx) => {
        const selected = value[group.id] ?? null;
        return (
          <div key={group.id} className={idx < groups.length - 1 ? 'mb-3' : ''}>
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              {t(group.labelKey)}
            </div>
            <div className="flex flex-wrap gap-1.5" role="radiogroup">
              {group.options.map((opt) => {
                const isSelected = selected === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    onClick={() => onChange(group.id, isSelected ? null : opt.value)}
                    className={`px-3.5 py-1.5 rounded-full text-[13px] font-medium border-[1.5px] transition-all active:scale-95 select-none ${
                      isSelected
                        ? 'bg-amber-100 border-amber-500 text-amber-700 font-semibold'
                        : 'bg-stone-100 border-gray-200 text-gray-500'
                    }`}
                  >
                    {t(opt.labelKey)}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
